import React, { useState } from 'react';
import { Superscript, Subscript, Pi, SquareRadical, ChevronDown } from 'lucide-react';

const MathToolbar = ({ textareaRef, value, onChange }) => {
  const [showGreek, setShowGreek] = useState(false);
  const [showMath, setShowMath] = useState(false);
  const [showSuperscript, setShowSuperscript] = useState(false);
  const [showSubscript, setShowSubscript] = useState(false);

  // Greek letters
  const greekLetters = [
    { lower: 'α', upper: 'Α', name: 'alpha' },
    { lower: 'β', upper: 'Β', name: 'beta' },
    { lower: 'γ', upper: 'Γ', name: 'gamma' },
    { lower: 'δ', upper: 'Δ', name: 'delta' },
    { lower: 'ε', upper: 'Ε', name: 'epsilon' },
    { lower: 'ζ', upper: 'Ζ', name: 'zeta' },
    { lower: 'η', upper: 'Η', name: 'eta' },
    { lower: 'θ', upper: 'Θ', name: 'theta' },
    { lower: 'ι', upper: 'Ι', name: 'iota' },
    { lower: 'κ', upper: 'Κ', name: 'kappa' },
    { lower: 'λ', upper: 'Λ', name: 'lambda' },
    { lower: 'μ', upper: 'Μ', name: 'mu' },
    { lower: 'ν', upper: 'Ν', name: 'nu' },
    { lower: 'ξ', upper: 'Ξ', name: 'xi' },
    { lower: 'π', upper: 'Π', name: 'pi' },
    { lower: 'ρ', upper: 'Ρ', name: 'rho' },
    { lower: 'σ', upper: 'Σ', name: 'sigma' },
    { lower: 'τ', upper: 'Τ', name: 'tau' },
    { lower: 'υ', upper: 'Υ', name: 'upsilon' },
    { lower: 'φ', upper: 'Φ', name: 'phi' },
    { lower: 'χ', upper: 'Χ', name: 'chi' },
    { lower: 'ψ', upper: 'Ψ', name: 'psi' },
    { lower: 'ω', upper: 'Ω', name: 'omega' },
  ];

  // Math symbols
  const mathSymbols = [
    { symbol: '√', name: 'square root' },
    { symbol: '∛', name: 'cube root' },
    { symbol: '±', name: 'plus minus' },
    { symbol: '×', name: 'multiply' },
    { symbol: '÷', name: 'divide' },
    { symbol: '≠', name: 'not equal' },
    { symbol: '≈', name: 'approximately' },
    { symbol: '≤', name: 'less than or equal' },
    { symbol: '≥', name: 'greater than or equal' },
    { symbol: '∞', name: 'infinity' },
    { symbol: '°', name: 'degree' },
    { symbol: '∑', name: 'sum' },
    { symbol: '∏', name: 'product' },
    { symbol: '∫', name: 'integral' },
    { symbol: '∂', name: 'partial' },
    { symbol: '∇', name: 'nabla' },
    { symbol: '∈', name: 'element of' },
    { symbol: '∉', name: 'not element of' },
    { symbol: '⊂', name: 'subset' },
    { symbol: '⊃', name: 'superset' },
    { symbol: '∪', name: 'union' },
    { symbol: '∩', name: 'intersection' },
    { symbol: '∠', name: 'angle' },
    { symbol: '⊥', name: 'perpendicular' },
    { symbol: '∥', name: 'parallel' },
    { symbol: '→', name: 'arrow right' },
    { symbol: '←', name: 'arrow left' },
    { symbol: '↔', name: 'arrow both' },
    { symbol: '⇌', name: 'equilibrium' },
  ];

  // Superscript characters
  const superscripts = [
    { normal: '0', super: '⁰' },
    { normal: '1', super: '¹' },
    { normal: '2', super: '²' },
    { normal: '3', super: '³' },
    { normal: '4', super: '⁴' },
    { normal: '5', super: '⁵' },
    { normal: '6', super: '⁶' },
    { normal: '7', super: '⁷' },
    { normal: '8', super: '⁸' },
    { normal: '9', super: '⁹' },
    { normal: '+', super: '⁺' },
    { normal: '-', super: '⁻' },
    { normal: '=', super: '⁼' },
    { normal: '(', super: '⁽' },
    { normal: ')', super: '⁾' },
    { normal: 'n', super: 'ⁿ' },
    { normal: 'x', super: 'ˣ' },
    { normal: 'y', super: 'ʸ' },
  ];

  // Subscript characters
  const subscripts = [
    { normal: '0', sub: '₀' },
    { normal: '1', sub: '₁' },
    { normal: '2', sub: '₂' },
    { normal: '3', sub: '₃' },
    { normal: '4', sub: '₄' },
    { normal: '5', sub: '₅' },
    { normal: '6', sub: '₆' },
    { normal: '7', sub: '₇' },
    { normal: '8', sub: '₈' },
    { normal: '9', sub: '₉' },
    { normal: '+', sub: '₊' },
    { normal: '-', sub: '₋' },
    { normal: '=', sub: '₌' },
    { normal: '(', sub: '₍' },
    { normal: ')', sub: '₎' },
    { normal: 'a', sub: 'ₐ' },
    { normal: 'e', sub: 'ₑ' },
    { normal: 'o', sub: 'ₒ' },
    { normal: 'x', sub: 'ₓ' },
    { normal: 'n', sub: 'ₙ' },
    { normal: 'm', sub: 'ₘ' },
  ];

  const insertSymbol = (symbol) => {
    if (textareaRef?.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + symbol + value.substring(end);
      onChange(newValue);
      
      // Set cursor position after the inserted symbol
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + symbol.length, start + symbol.length);
      }, 0);
    } else {
      onChange(value + symbol);
    }
    
    // Close all dropdowns
    setShowGreek(false);
    setShowMath(false);
    setShowSuperscript(false);
    setShowSubscript(false);
  };

  const closeAllDropdowns = () => {
    setShowGreek(false);
    setShowMath(false);
    setShowSuperscript(false);
    setShowSubscript(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border border-slate-200 border-b-0 rounded-t-lg">
      {/* Superscript Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { closeAllDropdowns(); setShowSuperscript(!showSuperscript); }}
          className={`flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded transition-all ${
            showSuperscript ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Superscript"
        >
          x<sup className="text-xs">2</sup>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showSuperscript && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-slate-200 z-50 min-w-[180px]">
            <div className="text-xs text-slate-500 mb-2 font-medium">Superscript</div>
            <div className="grid grid-cols-6 gap-1">
              {superscripts.map((s) => (
                <button
                  key={s.normal}
                  type="button"
                  onClick={() => insertSymbol(s.super)}
                  className="w-7 h-7 flex items-center justify-center text-lg hover:bg-indigo-100 rounded transition-all"
                  title={s.normal}
                >
                  {s.super}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Subscript Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { closeAllDropdowns(); setShowSubscript(!showSubscript); }}
          className={`flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded transition-all ${
            showSubscript ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Subscript"
        >
          x<sub className="text-xs">2</sub>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showSubscript && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-slate-200 z-50 min-w-[180px]">
            <div className="text-xs text-slate-500 mb-2 font-medium">Subscript</div>
            <div className="grid grid-cols-6 gap-1">
              {subscripts.map((s) => (
                <button
                  key={s.normal}
                  type="button"
                  onClick={() => insertSymbol(s.sub)}
                  className="w-7 h-7 flex items-center justify-center text-lg hover:bg-indigo-100 rounded transition-all"
                  title={s.normal}
                >
                  {s.sub}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-slate-300 mx-1" />

      {/* Greek Letters Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { closeAllDropdowns(); setShowGreek(!showGreek); }}
          className={`flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded transition-all ${
            showGreek ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Greek Letters"
        >
          <span className="text-base">α β γ</span>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showGreek && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-slate-200 z-50 min-w-[280px]">
            <div className="text-xs text-slate-500 mb-2 font-medium">Greek Letters (lowercase / UPPERCASE)</div>
            <div className="grid grid-cols-8 gap-1">
              {greekLetters.map((g) => (
                <React.Fragment key={g.name}>
                  <button
                    type="button"
                    onClick={() => insertSymbol(g.lower)}
                    className="w-7 h-7 flex items-center justify-center text-lg hover:bg-indigo-100 rounded transition-all"
                    title={g.name}
                  >
                    {g.lower}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="border-t border-slate-200 mt-2 pt-2">
              <div className="grid grid-cols-8 gap-1">
                {greekLetters.map((g) => (
                  <button
                    key={g.name + '-upper'}
                    type="button"
                    onClick={() => insertSymbol(g.upper)}
                    className="w-7 h-7 flex items-center justify-center text-lg hover:bg-indigo-100 rounded transition-all"
                    title={g.name + ' (uppercase)'}
                  >
                    {g.upper}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Math Symbols Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => { closeAllDropdowns(); setShowMath(!showMath); }}
          className={`flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded transition-all ${
            showMath ? 'bg-indigo-100 text-indigo-700' : 'text-slate-600 hover:bg-slate-200'
          }`}
          title="Math Symbols"
        >
          <span className="text-base">√ ± ∞</span>
          <ChevronDown className="w-3 h-3" />
        </button>
        {showMath && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-slate-200 z-50 min-w-[280px]">
            <div className="text-xs text-slate-500 mb-2 font-medium">Math & Science Symbols</div>
            <div className="grid grid-cols-8 gap-1">
              {mathSymbols.map((m) => (
                <button
                  key={m.symbol}
                  type="button"
                  onClick={() => insertSymbol(m.symbol)}
                  className="w-7 h-7 flex items-center justify-center text-lg hover:bg-indigo-100 rounded transition-all"
                  title={m.name}
                >
                  {m.symbol}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick access common symbols */}
      <div className="w-px h-6 bg-slate-300 mx-1" />
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => insertSymbol('²')}
          className="w-7 h-7 flex items-center justify-center text-sm font-medium text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Square (²)"
        >
          x²
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('³')}
          className="w-7 h-7 flex items-center justify-center text-sm font-medium text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Cube (³)"
        >
          x³
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('π')}
          className="w-7 h-7 flex items-center justify-center text-lg text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Pi (π)"
        >
          π
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('θ')}
          className="w-7 h-7 flex items-center justify-center text-lg text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Theta (θ)"
        >
          θ
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('Δ')}
          className="w-7 h-7 flex items-center justify-center text-lg text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Delta (Δ)"
        >
          Δ
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('√')}
          className="w-7 h-7 flex items-center justify-center text-lg text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Square Root (√)"
        >
          √
        </button>
        <button
          type="button"
          onClick={() => insertSymbol('°')}
          className="w-7 h-7 flex items-center justify-center text-lg text-slate-600 hover:bg-slate-200 rounded transition-all"
          title="Degree (°)"
        >
          °
        </button>
      </div>
    </div>
  );
};

export default MathToolbar;
